import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { MeterBucket, RegisterChip } from '@metron/shared';
import {
  parseMeterChips,
  parseRegisterChips,
  PRODUCT_TYPE_FILTER_PARAMS,
  serializeChips,
} from '@metron/shared';

export interface UseProductTypeFiltersResult {
  registerBuckets: RegisterChip[];
  meterBuckets: MeterBucket[];
  setRegister: (values: RegisterChip[]) => void;
  setMeter: (values: MeterBucket[]) => void;
}

export function useProductTypeFilters(): UseProductTypeFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const registerBuckets = useMemo(
    () => parseRegisterChips(searchParams.get(PRODUCT_TYPE_FILTER_PARAMS.register)),
    [searchParams],
  );
  const meterBuckets = useMemo(
    () => parseMeterChips(searchParams.get(PRODUCT_TYPE_FILTER_PARAMS.meter)),
    [searchParams],
  );

  function writeParam(key: string, serialized: string | null) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (serialized === null) next.delete(key);
        else next.set(key, serialized);
        return next;
      },
      { replace: true },
    );
  }

  function setRegister(values: RegisterChip[]) {
    writeParam(PRODUCT_TYPE_FILTER_PARAMS.register, serializeChips(values));
  }
  function setMeter(values: MeterBucket[]) {
    writeParam(PRODUCT_TYPE_FILTER_PARAMS.meter, serializeChips(values));
  }

  return { registerBuckets, meterBuckets, setRegister, setMeter };
}
